import {PageHeader} from "../../shared/ui";
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import {useNavigate} from "react-router-dom";
import {
    Building,
    CreditCard,
    FileText,
    LayoutDashboard,
    Monitor,
    Package,
    Computer, Gauge, ShieldCheck,
    RefreshCw
} from "lucide-react";
import React, { useState } from "react";
import { motion } from "motion/react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Typography,
    useTheme
} from "@mui/material";
import {useHomeDashboard} from "./useHomeDashboard";
import {formatMoneyBR} from "./homeUtils";
import {useAcessoAUsuarios} from "../Users/useAcessoAUsuarios";

export const HomePage = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dashboard = useHomeDashboard();
    const acessoAUsuarios = useAcessoAUsuarios();
    const [open, setOpen] = useState(true);
    const [title, setTitle] = useState("");
    const [menus, setMenus] = useState([
        {label: "Dashboard", id: "dashboard", selected: true, disabled: false, icon: <LayoutDashboard size={20} />, onClick: () => navigate("/")},
        {label: "CRM", id: "crm", disabled: false, icon: <BusinessIcon />, onClick:() => navigate("/empresas/dashboard")},
        {label: "Sistemas", id: "sistemas", disabled: false, icon: <Computer />, onClick: () => navigate("/sistemas")},
        {label: "Métricas", id: "metricas", disabled: false, icon: <Gauge />, onClick: () => navigate("/metricas")},
        {label: "Planos Comerciais", id: "planos", disabled: false, icon: <Package />, onClick: () => navigate("/planos-comerciais")},
        {label: "Licenças", id: "licencas", disabled: false, icon: <ShieldCheck />, onClick: () => navigate("/licencas/dashboard")},
        {label: "Financeiro", id: "financeiro", disabled: false, icon: <CreditCard />, onClick: () => navigate("/financeiro/dashboard")},
        {label: "Gestão de Usuários", id: "gestao-de-usuarios", disabled: false, icon: <PersonIcon />, onClick: () => navigate("/usuarios")},
        {label: "Monitoramento", id: "monitoramento", disabled: false, icon: <Monitor />}
    ])
    const handleMenuClose = (isOpen) => setOpen(isOpen)
    const handleMenuOpen = (id) => {
        setMenus(menus.map(item => ({
            ...item,
            selected: item.id === id
        })))
        setTitle(menus.find((menu) => menu.id === id)?.label);
    }

    const dashboardMetrics = [
        { title: 'Empresas Ativas', value: dashboard.metrics.empresasAtivas, helper: 'clientes cadastrados', icon: <Building size={24} /> },
        { title: 'Sistemas Ativos', value: dashboard.metrics.sistemasAtivos, helper: 'sistemas cadastrados', icon: <Monitor size={24} /> },
        { title: 'Assinaturas', value: dashboard.metrics.assinaturas, helper: 'licenças cadastradas', icon: <FileText size={24} /> },
        { title: 'Receita Mensal', value: formatMoneyBR(dashboard.metrics.receitaMensal), helper: 'MRR financeiro', icon: <CreditCard size={24} /> },
    ];

    return (
        <Box
            sx={{ display: 'flex', minHeight: '100dvh' }}
        >
            <PageHeader
                title={title ? title : "BusiFarm"}
                subtitle={title ? "" : "Gestão Corporativa"}
                titleAction={() => navigate("/")}
                itemsMenu={menus.filter((menu) => menu.id !== 'gestao-de-usuarios' || acessoAUsuarios.pode)}
                onHandleMenuOpen={handleMenuOpen}
                onHandleDrawerToggle={handleMenuClose}
                openParent={open}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 6,
                    minWidth: 0,
                    boxSizing: 'border-box',
                    transition: theme.transitions.create(['margin', 'width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    mt: 8,
                    backgroundColor: theme.palette.background.default,
                    minHeight: 'calc(100dvh - 64px)',
                }}
            >

                <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
                    Dashboard
                </Typography>

                <Typography variant="body1" color="text.secondary" paragraph>
                    Bem-vindo ao painel administrativo do BusiFarm. Aqui você pode gerenciar empresas, sistemas, assinaturas e muito mais.
                </Typography>

                {dashboard.error ? (
                    <Alert severity="warning" sx={{mb: 3}} action={(
                        <Button color="inherit" size="small" startIcon={<RefreshCw size={15} />} onClick={dashboard.reload}>
                            Recarregar
                        </Button>
                    )}>
                        {dashboard.error}
                    </Alert>
                ) : null}

                <Grid container spacing={3} sx={{ mb: 4, mt: 1 }}>
                    {dashboardMetrics.map((metric, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.1 }}
                            >
                                <Card sx={{ height: '100%' }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">
                                                    {metric.title}
                                                </Typography>
                                                <Typography variant="h5" component="div" sx={{ mt: 1, fontWeight: 700 }}>
                                                    {dashboard.loading ? <CircularProgress size={22} /> : metric.value}
                                                </Typography>
                                                <Chip
                                                    label={metric.helper}
                                                    size="small"
                                                    color="default"
                                                    sx={{ mt: 1, height: 24, fontSize: '0.75rem' }}
                                                />
                                            </Box>
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    bgcolor: `${theme.palette.primary.main}15`,
                                                    color: theme.palette.primary.main
                                                }}
                                            >
                                                {metric.icon}
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Box>
    );
}
